import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { fontStyles } from '@/styles/typography';

import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { classNames } from '@/utils/classnames';
import { getPriceImpactThreshold } from '@/utils/liquidityScore';
import { ZoraType } from '@/utils/zora';

import { DropletsIcon } from './Droplets';

export interface ScoreScaleItem {
  /** The amount of liquidity */
  amount: number;
  /** The score value */
  score: number;
  /** The icon to display */
  icon: React.ReactNode;
}

export const SCORE_SCALE: ScoreScaleItem[] = [
  {
    amount: 50_000_000,
    score: 10,
    icon: <DropletsIcon.High />,
  },
  {
    amount: 10_000_000,
    score: 9,
    icon: <DropletsIcon.High />,
  },
  {
    amount: 5_000_000,
    score: 8,
    icon: <DropletsIcon.High />,
  },
  {
    amount: 1_000_000,
    score: 7,
    icon: <DropletsIcon.High />,
  },
  {
    amount: 500_000,
    score: 6,
    icon: <DropletsIcon.High />,
  },
  {
    amount: 100_000,
    score: 5,
    icon: <DropletsIcon.Medium />,
  },
  {
    amount: 50_000,
    score: 4,
    icon: <DropletsIcon.Medium />,
  },
  {
    amount: 10_000,
    score: 3,
    icon: <DropletsIcon.Medium />,
  },
  {
    amount: 1_000,
    score: 2,
    icon: <DropletsIcon.Low />,
  },
  {
    amount: 100,
    score: 1,
    icon: <DropletsIcon.Low />,
  },
  {
    amount: 0,
    score: 0,
    icon: <DropletsIcon.Low />,
  },
];

const usdFallbackFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

interface ScoreTooltipProps {
  /** The content to display the tooltip for */
  children: React.ReactElement;
  /** Whether the tooltip is being displayed on a mobile device */
  isMobile?: boolean;
  /** The score of the current token */
  currentScore?: number;
  /** Zora token type */
  zoraType: ZoraType;
}

export const ScoreTooltip = ({ children, isMobile, currentScore, zoraType }: ScoreTooltipProps) => {
  const priceImpactThreshold = getPriceImpactThreshold(zoraType);
  const { formatFiat } = useFiatDisplay();
  const formatAmount = (amount: number) => formatFiat(amount, false, false);

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>{children}</MatchaTooltipTrigger>

      <MatchaTooltipContent>
        <div>
          {!isMobile && (
            <h5 className={fontStyles({ variant: 'LABEL_SM', class: ['mb-3'] })}>
              Liquidity Score
            </h5>
          )}

          <p className="text-token-gray-800 border-b border-b-special-border-divider border-solid mb-3 pb-2">
            A measure of the liquidity available within {priceImpactThreshold}% of the spot price*
          </p>

          <ul>
            <li className="flex justify-between mb-2 text-token-gray-800">
              <span>Score</span>
              <span>{priceImpactThreshold}% Depth</span>
            </li>
            {SCORE_SCALE.map(({ amount, icon, score }: ScoreScaleItem) => (
              <ScaleItem
                key={`score-${score}`}
                amount={amount}
                currentScore={currentScore}
                score={score}
                icon={icon}
                isMobile={isMobile}
                formatAmount={formatAmount}
              />
            ))}
          </ul>
        </div>
      </MatchaTooltipContent>

      <MatchaTooltipPopover
        align="start"
        className="py-3 max-w-[246px]"
        side="right"
        sideOffset={8}
      />
      <MatchaTooltipBottomSheet header="Liquidity Score" />
    </MatchaTooltip>
  );
};

interface ScaleItemProps {
  /** notional amount */
  amount: number;
  /** The score of the current token */
  currentScore?: number;
  /** The icon to display */
  icon: React.ReactNode;
  /** The score value */
  score: number;
  /** Whether the tooltip is being displayed on a mobile device */
  isMobile?: boolean;
  /** Additional class name. */
  className?: string;
  /** Format function for the amount. Falls back to USD formatter if not provided. */
  formatAmount?: (amount: number) => string;
}

export const ScaleItem = ({
  amount,
  currentScore,
  icon,
  isMobile,
  score,
  className,
  formatAmount,
}: ScaleItemProps): React.ReactElement => {
  const isCurrentScore = currentScore === score;

  return (
    <li
      className={classNames(
        'flex items-center justify-between h-[22px] mt-1',
        isCurrentScore
          ? 'text-token-gray-900bg-[linear-gradient(90deg,rgba(247,247,248,0)_0%,#F7F7F8_10%,#F7F7F8_90%,rgba(247,247,248,0)_100%)] font-medium'
          : '',
        className,
      )}
    >
      <div className="flex items-center">
        <span className="inline-block mr-1 h-4 w-4">{icon}</span>
        <span>{score}</span>
      </div>

      <div className={classNames('flex-grow text-right', isMobile ? 'text-350' : '')}>
        {formatAmount ? formatAmount(amount) : usdFallbackFormatter.format(amount)}
      </div>
    </li>
  );
};
